/**
 * The per-window owner of the keybinding table (架构方案 §5.9).
 *
 * Mounted around *every* top-level surface — workbench, detached window, module
 * shell, app gate — which is the point: chords used to be installed by the
 * workbench alone, so a popped-out view had none (见 08 已知缺口). Everything a
 * surface needs to differ by is a `when` key (`workbench.surface`), not a
 * different install site.
 */

import type { ChannelClient } from '@hibro/platform-core';
import type {
  GlobalShortcutStatus,
  IKeybindingService,
  IModuleService,
  KeybindingOverride,
  ModuleRuntime,
  ResolvedCommand,
  ResolvedKeybinding,
  WhenContext,
} from '@hibro/shared';
import {
  CONTRIBUTIONS_CHANGED_EVENT,
  KEYBINDINGS_CHANGED_EVENT,
  type KeybindingsChanged,
} from '@hibro/shared';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { BUILTIN_COMMANDS } from '../commands/builtin-commands';
import { builtinKeybindings } from './builtin-keybindings';
import {
  isMacPlatform,
  type KeyboardLayoutLabels,
  readKeyboardLayout,
} from './chord';
import { installKeybindingDispatcher } from './dispatcher';
import {
  type CompiledKeybinding,
  findKeybindingConflicts,
  globalBindings,
  type KeybindingConflict,
  mainWindowShortcuts,
  resolveKeybindings,
  windowBindings,
} from './registry';

/**
 * Which top-level surface this window is showing, published as the
 * `workbench.surface` context key so a chord can be narrowed to one of them.
 */
export type KeybindingSurface = 'workbench' | 'detached' | 'shell' | 'gate';

/** Platform `when` key naming the surface; see {@link KeybindingSurface}. */
export const SURFACE_CONTEXT_KEY = 'workbench.surface';

/**
 * What the mounted surface contributes to dispatch: the live `when` context, the
 * command table (for `enablement`) and the dispatcher.
 *
 * Registered by the surface rather than passed in from the boot sequence,
 * because only the workbench knows its active view, and only it holds the
 * built-in command handlers its own chords need to reach.
 */
export interface KeybindingDispatchInputs {
  getContext: () => WhenContext;
  getCommands: () => readonly ResolvedCommand[];
  executeCommand: (command: string, ...args: unknown[]) => void;
}

export interface KeybindingRegistryValue {
  /** Every declared binding, compiled, in ascending precedence. */
  readonly bindings: readonly CompiledKeybinding[];
  /**
   * Every declared command, so a face can name what a chord runs. Read from the
   * same `listContributions` call the table came from, rather than fetched
   * again: the two must describe the same set of modules.
   */
  readonly commands: readonly ResolvedCommand[];
  readonly conflicts: readonly KeybindingConflict[];
  /** OS registration outcomes; empty where there are no OS grabs. */
  readonly globalStatus: readonly GlobalShortcutStatus[];
  /** Current keyboard layout for labelling, absent where unsupported. */
  readonly layout?: KeyboardLayoutLabels;
  readonly mac: boolean;
  /** Whether this runtime can hold OS-level chords at all. */
  readonly supportsGlobal: boolean;
  /** Absent when the host has no keybinding service (a disconnected page). */
  readonly setOverride?: (override: KeybindingOverride) => Promise<void>;
  readonly reset?: (id: string) => Promise<void>;
  readonly resetAll?: () => Promise<void>;
  /** Register this surface's dispatch inputs; returns a disposer. */
  readonly provideDispatchInputs: (
    inputs: KeybindingDispatchInputs,
  ) => () => void;
}

const NOOP_REGISTRY: KeybindingRegistryValue = {
  bindings: [],
  commands: [],
  conflicts: [],
  globalStatus: [],
  mac: false,
  supportsGlobal: false,
  provideDispatchInputs: () => () => undefined,
};

const KeybindingRegistryContext =
  createContext<KeybindingRegistryValue>(NOOP_REGISTRY);

/** Read the resolved table (the shortcuts settings page and chord hints). */
// eslint-disable-next-line react-refresh/only-export-components
export function useKeybindings(): KeybindingRegistryValue {
  return useContext(KeybindingRegistryContext);
}

/**
 * Hand this surface's `when` context, command table and dispatcher to the host.
 *
 * `inputs` is read through a ref on every keystroke, so a surface may pass a
 * freshly-built object each render without re-installing anything.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useKeybindingDispatch(inputs: KeybindingDispatchInputs): void {
  const { provideDispatchInputs } = useKeybindings();
  const latest = useRef(inputs);
  latest.current = inputs;
  useEffect(
    () =>
      provideDispatchInputs({
        getContext: () => latest.current.getContext(),
        getCommands: () => latest.current.getCommands(),
        executeCommand: (command, ...args) =>
          latest.current.executeCommand(command, ...args),
      }),
    [provideDispatchInputs],
  );
}

export interface KeybindingHostProps {
  moduleService: IModuleService;
  /** Absent on a page whose host has no keybinding channel (older host). */
  keybindingService?: IKeybindingService;
  client: ChannelClient;
  runtime: ModuleRuntime;
  surface: KeybindingSurface;
  /** Dev build; gates the reload chords the same way the main process did. */
  dev?: boolean;
  mac?: boolean;
  /** Record a diagnostic for a chord that cannot work (见 15). */
  log?: (message: string) => void;
  children: ReactNode;
}

/**
 * Resolve, install and publish this window's chords.
 *
 * Three inputs land here — the built-in table, `listContributions`, and the
 * user's overrides — and exactly one table comes out, which is what keeps the
 * shortcuts page, the chord hints in search results and the actual keyboard
 * from disagreeing.
 */
export function KeybindingHost({
  moduleService,
  keybindingService,
  client,
  runtime,
  surface,
  dev,
  mac,
  log,
  children,
}: KeybindingHostProps): ReactNode {
  const [contributed, setContributed] = useState<readonly ResolvedKeybinding[]>(
    [],
  );
  const [contributedCommands, setContributedCommands] = useState<
    readonly ResolvedCommand[]
  >([]);
  const [overrides, setOverrides] = useState<readonly KeybindingOverride[]>([]);
  const [globalStatus, setGlobalStatus] = useState<
    readonly GlobalShortcutStatus[]
  >([]);
  const [layout, setLayout] = useState<KeyboardLayoutLabels | undefined>(
    undefined,
  );
  const inputsRef = useRef<KeybindingDispatchInputs | undefined>(undefined);
  const [, setInputsVersion] = useState(0);

  useEffect(() => {
    let active = true;
    const refresh = (): void => {
      void moduleService
        .listContributions()
        .then((contributions) => {
          if (active) {
            setContributed(contributions.keybindings);
            setContributedCommands(contributions.commands);
          }
        })
        .catch((error: unknown) => {
          log?.(
            `keybindings: listContributions failed (${describe(error)}); module chords are missing until the next refresh`,
          );
        });
    };
    refresh();
    const dispose = client.listen(CONTRIBUTIONS_CHANGED_EVENT, refresh);
    return () => {
      active = false;
      dispose();
    };
  }, [moduleService, client, log]);

  useEffect(() => {
    if (!keybindingService) {
      return;
    }
    let active = true;
    const apply = (next: readonly KeybindingOverride[]): void => {
      if (active) {
        setOverrides(next);
      }
    };
    void keybindingService
      .getOverrides()
      .then(apply)
      .catch((error: unknown) => {
        log?.(
          `keybindings: reading user overrides failed (${describe(error)}); showing the declared chords instead`,
        );
      });
    void keybindingService
      .getGlobalStatus()
      .then((statuses) => {
        if (active) {
          setGlobalStatus(statuses);
        }
      })
      .catch(() => undefined);
    const dispose = client.listen(KEYBINDINGS_CHANGED_EVENT, (data) => {
      apply((data as KeybindingsChanged).overrides);
    });
    return () => {
      active = false;
      dispose();
    };
  }, [keybindingService, client, log]);

  useEffect(() => {
    let active = true;
    void readKeyboardLayout().then((map) => {
      if (active && map) {
        setLayout(map);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  // The platform's own commands belong in the same table as the modules'
  // (the merge the workbench already does for search and menus). Without them
  // a built-in chord has no declaration to read: its `enablement` is invisible
  // to the dispatcher, and the shortcuts page can only print the raw id.
  const commandTable = useMemo(
    () => [...BUILTIN_COMMANDS, ...contributedCommands],
    [contributedCommands],
  );

  const isMac = mac ?? isMacPlatform();
  const compiled = useMemo(
    () =>
      resolveKeybindings({
        builtin: builtinKeybindings({ dev: dev ?? false, mac: isMac }),
        contributed,
        overrides,
        web: runtime === 'web',
        chordOptions: { mac: isMac },
      }).filter((entry) => supportedHere(entry, runtime)),
    [contributed, overrides, runtime, isMac, dev],
  );

  // Every compiled problem is a chord that silently does nothing when pressed,
  // so each one is stated once per resolve rather than left for the user to
  // discover by pressing it (见 15 / no-implicit-fallbacks).
  useEffect(() => {
    for (const entry of compiled) {
      if (entry.problem !== undefined) {
        log?.(
          `keybindings: "${entry.binding.key}" for ${entry.binding.command} is inactive (${entry.problem})`,
        );
      }
    }
  }, [compiled, log]);

  const listened = useMemo(() => windowBindings(compiled), [compiled]);

  useEffect(() => {
    return installKeybindingDispatcher(listened, {
      executeCommand: (command, ...args) => {
        const inputs = inputsRef.current;
        if (inputs) {
          inputs.executeCommand(command, ...args);
          return;
        }
        void moduleService.executeCommand(command, ...args);
      },
      getContext: () => ({
        ...(inputsRef.current?.getContext() ?? {}),
        [SURFACE_CONTEXT_KEY]: surface,
      }),
      commands: commandTable,
    });
  }, [listened, commandTable, moduleService, surface]);

  // Only the workbench publishes: the OS grabs and the main-process chords are
  // per-application, and a detached window republishing the same table would
  // make the last window to mount the one that owns them.
  const publishes = surface === 'workbench' && runtime === 'desktop';
  useEffect(() => {
    if (!publishes || !keybindingService) {
      return;
    }
    void keybindingService
      .publishShortcuts({
        global: globalBindings(compiled),
        mainWindow: mainWindowShortcuts(compiled),
      })
      .then(setGlobalStatus)
      .catch((error: unknown) => {
        log?.(
          `keybindings: publishing OS shortcuts failed (${describe(error)}); global chords are not registered`,
        );
      });
  }, [publishes, keybindingService, compiled, log]);

  const provideDispatchInputs = useCallback(
    (inputs: KeybindingDispatchInputs) => {
      inputsRef.current = inputs;
      setInputsVersion((version) => version + 1);
      return () => {
        if (inputsRef.current === inputs) {
          inputsRef.current = undefined;
        }
      };
    },
    [],
  );

  const value = useMemo<KeybindingRegistryValue>(
    () => ({
      bindings: compiled,
      commands: commandTable,
      conflicts: findKeybindingConflicts(compiled),
      globalStatus,
      ...(layout ? { layout } : {}),
      mac: isMac,
      supportsGlobal: runtime === 'desktop',
      ...(keybindingService
        ? {
            setOverride: async (override: KeybindingOverride) => {
              await keybindingService.setOverride(override);
            },
            reset: async (id: string) => {
              await keybindingService.reset(id);
            },
            resetAll: async () => {
              await keybindingService.resetAll();
            },
          }
        : {}),
      provideDispatchInputs,
    }),
    [
      compiled,
      commandTable,
      globalStatus,
      layout,
      isMac,
      runtime,
      keybindingService,
      provideDispatchInputs,
    ],
  );

  return (
    <KeybindingRegistryContext.Provider value={value}>
      {children}
    </KeybindingRegistryContext.Provider>
  );
}

/**
 * Whether a resolved binding belongs on this runtime.
 *
 * Contributed bindings were already narrowed by the host (which knows each
 * module's manifest); this catches the built-in table, whose `runtimes` no
 * manifest covers.
 */
function supportedHere(
  entry: CompiledKeybinding,
  runtime: ModuleRuntime,
): boolean {
  const declared = entry.binding.runtimes;
  return declared === undefined || declared.includes(runtime);
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
